import React, { useEffect, useState } from "react";
import { Download, X, Loader2 } from "lucide-react";
import PDFFormEditor from "./PDFFormEditor";

const PreviewModal = ({ previewDoc, closePreview, onSave }) => {
  const allowSave = Boolean(previewDoc?.canSave && onSave);
  const [saveName, setSaveName] = useState("");
  const [saveStatus, setSaveStatus] = useState("idle");
  const [modifiedPdfBlob, setModifiedPdfBlob] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setSaveName(previewDoc?.saveName || previewDoc?.name || "");
    setSaveStatus("idle");
    setModifiedPdfBlob(null);
    setImageError(false);
  }, [previewDoc]);

  const handleSave = async () => {
    const isPdf = previewDoc?.type === "application/pdf";
    const canSave = (allowSave || (isPdf && modifiedPdfBlob)) && onSave;

    if (!canSave || !saveName.trim()) return;
    setSaveStatus("saving");

    let tempUrl = null;
    try {
      let urlToSave = previewDoc.url;
      if (modifiedPdfBlob) {
        tempUrl = URL.createObjectURL(modifiedPdfBlob);
        urlToSave = tempUrl;
      }

      const ok = await onSave({
        name: saveName.trim(),
        url: urlToSave,
        type: previewDoc.type,
        blob: modifiedPdfBlob
      });

      setSaveStatus(ok === false ? "error" : "saved");

      if (ok !== false) {
        setTimeout(() => {
          closePreview();
        }, 1000);
      }
    } catch (error) {
      console.error("Error saving document:", error);
      setSaveStatus("error");
    } finally {
      if (tempUrl) {
        setTimeout(() => {
          URL.revokeObjectURL(tempUrl);
        }, 5000);
      }
    }
  };

  if (!previewDoc) return null;

  const isPdf = previewDoc.type === "application/pdf";
  const showSaveUI = (allowSave || (isPdf && modifiedPdfBlob)) && onSave;

  return (
    <div className="fixed inset-0 z-[160] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-950/90 backdrop-blur-md transition-opacity"
        onClick={closePreview}
      ></div>

      {/* Modal Container */}
      <div className="relative w-full max-w-[90vw] h-[92vh] bg-slate-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-700 ring-1 ring-white/10 animate-scale-in">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-slate-950 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-2.5 bg-sky-500/10 rounded-lg border border-sky-500/20 text-sky-400">
              {isPdf ? <div className="font-bold text-xs">PDF</div> : <Download className="w-5 h-5" />}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-0.5">
                Document Preview
              </p>
              <h3 className="text-lg font-bold text-white truncate max-w-xl shadow-sm">
                {previewDoc.name}
              </h3>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href={previewDoc.url}
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-800 border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-700 hover:border-slate-600 transition-all shadow-sm"
              title="Open or download"
            >
              <Download className="w-4 h-4" />
              <span>Download</span>
            </a>
            <div className="w-px h-8 bg-slate-800 mx-2 hidden sm:block"></div>
            <button
              onClick={closePreview}
              className="p-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Save Bar */}
        {showSaveUI && (
          <div className="flex flex-wrap items-center gap-4 px-6 py-3 border-b border-slate-800 bg-slate-900/50 backdrop-blur shrink-0">
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <label className="absolute -top-2 left-2 px-1 bg-slate-900 text-[10px] font-semibold text-slate-400">
                  Save as
                </label>
                <input
                  value={saveName}
                  onChange={(event) => setSaveName(event.target.value)}
                  className="w-full rounded-lg px-4 py-2 bg-slate-950 border border-slate-700 text-white text-sm focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors placeholder-slate-600"
                  placeholder="Enter document name..."
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={!saveName.trim() || saveStatus === "saving"}
                className="px-6 py-2 rounded-lg bg-sky-600 text-white text-sm font-bold hover:bg-sky-500 shadow-lg shadow-sky-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saveStatus === "saving" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save to Case"
                )}
              </button>
              {saveStatus === "saved" && (
                <div className="flex items-center gap-1 text-emerald-400 text-sm font-medium animate-fade-in">
                  <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                  Saved
                </div>
              )}
              {saveStatus === "error" && (
                <div className="flex items-center gap-1 text-red-400 text-sm font-medium animate-fade-in">
                  <span className="w-2 h-2 rounded-full bg-red-400"></span>
                  Failed
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-hidden bg-slate-950 relative flex items-center justify-center">
          {previewDoc.type?.startsWith("image/") ? (
            <div className="w-full h-full overflow-auto p-8 flex items-center justify-center">
              {imageError ? (
                <div className="text-center p-8">
                  <p className="text-slate-400 mb-4">Image could not be displayed in preview.</p>
                  <a
                    href={previewDoc.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Open in New Tab
                  </a>
                </div>
              ) : (
                <img
                  src={previewDoc.url}
                  alt={previewDoc.name}
                  className="max-w-full max-h-full object-contain shadow-2xl rounded-lg border border-slate-800"
                  onError={(e) => {
                    console.error("Image failed to load:", previewDoc.url);
                    setImageError(true);
                  }}
                />
              )}
            </div>
          ) : previewDoc.type === "application/pdf" ? (
            <PDFFormEditor
              pdfUrl={previewDoc.url}
              onPdfModified={setModifiedPdfBlob}
            />
          ) : (
            <iframe
              src={previewDoc.url}
              className="w-full h-full border-0 bg-white"
              title={previewDoc.name}
            ></iframe>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
